import * as React from 'react';
import './Node.css';
import * as dagre from 'dagre';
import GraphViewPort from './GraphViewPort';
import HorizontalEdge from './HorizontalEdge';
import Modal from '../modal/Modal';
import applySugiyamaLayout from '../../utils/GraphDrawingSugiyama';
import applyKlayLayout from '../../utils/GraphDrawingKlay';
import Loading from '../../components/loading/Loading';
import ContextMenu from '../../components/contextMenu/ContextMenu';
import { Topic } from '../../containers/topicPage/Topic'
import AuthContext from '../../contexts/AuthContext';
import { useGraph } from '../../contexts/GraphContext';
import MergeNodeModal from '../modal/MergeNodeModal';
import AddConnectionModal from '../modal/AddConnectionModal';
import AddNodeModal from '../modal/AddNodeModal';
import { useLocation } from 'react-router-dom';
import { Exercise } from '../../containers/exercisePage/Exercise';
import AuthService from '../../services/AuthService';

//var ReactDOM = require('react-dom');
//const neo4j = require('neo4j-driver');
//const placeholderPic = require('../../assets/images/placeholder3.png')

const Graph = ({ graphMode='', descendentsCount=-1, viewPortSizeW = window.innerWidth + 'px', viewPortSizeH = window.innerHeight + 'px', initScale=1, minScale_=0.08, cameraFocus='onNode' }) => {

    const { user } = React.useContext(AuthContext);
    const graphData = useGraph();
    const location = useLocation();

    const {
        selectedNode,
        setSelectedNode,
        scale,
        setScale,
        setPosition,
        setLoading,
        loading,
        setBoundaries,
        setDefaultBoundaries,
        setGraphSize,
        setAllowMovement,
        nodes,
        setNodes,
        edges,
        setEdges,
        setTrackName,
        graphType,
        setGraphType,
        setPathId,
        setTrackId,
        resetGraph,
    } = graphData;

    const [viewPortSize, setViewPortSize] = React.useState({ w: viewPortSizeW, h: viewPortSizeH });
    const [isHorizontal, setIsHorizontal] = React.useState(false);
    const [minScale, setMinScale] = React.useState(minScale_);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [modalContent, setModalContent] = React.useState('topic');

    React.useEffect(() => {
        const handleResize = () => {
            setViewPortSize({
                w: window.innerWidth + 'px',
                h: window.innerHeight + 'px',
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const removeIndirectConnections = (nodes, edges) => {
        // Create adjacency matrix
        const adjacencyMatrix = new Map();
        nodes.forEach((node) => {
            adjacencyMatrix.set(node.properties.topicId, new Set());
        });
        edges.forEach((edge) => {
            adjacencyMatrix.get(edge.start).add(edge.end);
        });

        // Check if there's another path between the nodes of the edge
        const hasIndirectConnection = (start, end) => {
            const possibleIntermediateNodes = adjacencyMatrix.get(start);
            for (const n of possibleIntermediateNodes) {
                if (n !== start && adjacencyMatrix.get(n).has(end)) {
                    return true;
                }
            }
            return false;
        };

        // Remove edges with an indirect connection
        const updatedEdges = edges.filter((edge) => !hasIndirectConnection(edge.start, edge.end));

        return { nodes, edges: updatedEdges };
    };

    const assignTopicStatus = (nodes) => {

        if (!user) {
            return nodes; // If no user is logged in, return nodes without status assignment
        }

        return nodes = nodes.map((node) => {
            let status = 0;

            if (user.topicStatus.learned.includes(parseInt(node.topicId))) {
                status = 2;
            }
            else if (user.topicStatus.maybe.includes(parseInt(node.topicId))) {
                status = 1;
            }
            else if (user.topicStatus.verified.includes(parseInt(node.topicId))) {
                status = 3;
            }

            return {
                ...node,
                topicStatus: status,
            };
        });
    }

    function assignEdgeType(edges, nodes) {
        return edges.map(edge => {
            const startNode = nodes.find(n => n.topicId === edge.start);
            const endNode = nodes.find(n => n.topicId === edge.end);
            const edgeType = (startNode?.topicStatus === 2 && endNode?.topicStatus === 0) ? 1 : endNode?.topicStatus;

            return {
                ...edge,
                startName: startNode ? startNode.name : null,
                endName: endNode ? endNode.name : null,
                edgeType: endNode ? edgeType : null
            };
        });
    }

    const focusOnInit = (focusX, focusY) => {

        const screenX = scale * -focusX + parseInt(viewPortSize.w, 10) / 2 - 100/2;
        const screenY = scale * -focusY + parseInt(viewPortSize.h, 10) / 2 - 45/2;

        setPosition({ x: screenX, y: screenY });

    }

    React.useEffect(() => {

        const fetchData = async () => {
            try {

                setLoading(true);
                setAllowMovement(true);

                let node = '';
                let path = '';
                let userId = '';

                if (graphMode === 'learnedItemOnly') {

                    node = [
                        ...user.topicStatus.learned.map(id => parseInt(id)),
                        ...user.topicStatus.maybe.map(id => parseInt(id)),
                    ];

                    // TODO: not sure why this don't work
                    //userId = user.userProfileId;

                } else {
                    // default behaviour
                    const urlSearchParams = new URLSearchParams(location.search);
                    node = urlSearchParams.get('node') || '';
                    path = urlSearchParams.get('path') || '';
                    userId = urlSearchParams.get('user') || '';

                    if (node === '' && path === '' && userId === '') {
                        if (user) {
                            if (user.mostRecentGraph.nodeId) {
                                node = String(user.mostRecentGraph.nodeId);
                            }
                            else if (user.mostRecentGraph.pathId) {
                                path = String(user.mostRecentGraph.pathId);
                            }
                            else { node = 0 } // Set default node here by replacing 0
                        }
                    }

                    setTrackName(null);
                    setTrackId(null);

                    if (userId !== '') {
                        setGraphType('user');
                    } else if (node !== '') {
                        setGraphType('topic');
                    } else if (path !== '') {
                        setGraphType('path');
                        setPathId(path);
                    }
                }

                // Set most recent graph, so user are redirected to this graph when navigate to home.
                if (user && (node || path)) {
                    // Checking the tree of user does not set the most recent visted graph
                    if (userId === "" && graphMode !== 'learnedItemOnly') {
                        await fetch(`/api/userdata/setMostRecentGraph`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                userId: user.userId,
                                type: node ? 'node' : 'path',
                                id: Number(node || path),
                            }),
                        });

                        if (node) {
                            user.mostRecentGraph.nodeId = node;
                            user.mostRecentGraph.pathId = null;
                        } else if (path) {
                            user.mostRecentGraph.pathId = path;
                            user.mostRecentGraph.nodeId = null;
                        }
                        AuthService.syncLocalUserSession(user);
                    }
                }

                const response = await fetch(`/api/home?node=${node}&path=${path}&descendentsCount=${descendentsCount}&userId=${userId}`);
                const data = await response.json();

                if (!data.nodes || data.nodes.length === 0) {
                    setAllowMovement(false);
                    graphData.setGraph(null, null, null, null);
                    setNodes([]);
                    setEdges([]);
                    return;
                }

                const { nodes: intermediateNodes, edges: intermediateEdges } = removeIndirectConnections(data.nodes, data.edges);
                let { updatedNodes, updatedEdges } = applyKlayLayout(intermediateNodes, intermediateEdges, isHorizontal);

                updatedNodes = assignTopicStatus(updatedNodes);
                updatedEdges = assignEdgeType(updatedEdges, updatedNodes);

                updatedNodes = updatedNodes.map(node => {
                    const connectedEdges = updatedEdges.filter(edge => edge.end === node.topicId);

                    // Check if the node has topicStatus === 0 and is not connected by any edge
                    if ((node.topicStatus === 0 || node.topicStatus === 1) && connectedEdges.length === 0) {
                        return {
                            ...node,
                            nextNode: true,
                        };
                    }

                    // Check if all edges connected to this node have edgeType === 1
                    const allEdgesType = connectedEdges.length === 0 ? false : connectedEdges.every(edge => edge.edgeType === 1);

                    return {
                        ...node,
                        nextNode: allEdgesType,
                    };
                });

                setNodes(updatedNodes);
                setEdges(updatedEdges);
                const { graphWidth, graphHeight } = computeGraphDimensions(updatedNodes);

                let minX = 0, maxX = 0, minY = 0, maxY = 0;
                let screenX = 0, screenY = 0;
                const viewPortWidth = parseInt(viewPortSize.w, 10);
                const viewPortHeight = parseInt(viewPortSize.h, 10);

                // Function to handle 'full' camera focus
                const applyFullFocus = () => {
                    const scaleX = viewPortWidth / graphWidth;
                    const scaleY = viewPortHeight / graphHeight;
                    const scale = Math.min(scaleX, scaleY) * 0.80; // Include some padding around the graph

                    // Calculate centering position
                    const screenX = (viewPortWidth - graphWidth * scale) / 2 - 100 / 2;
                    const screenY = (viewPortHeight - graphHeight * scale) / 2 - 45 / 2;

                    // Update scale and position
                    setScale(scale);
                    setPosition({ x: screenX, y: screenY });
                };

                // Find the searched topic and get its x and y position to focused by view port
                if (cameraFocus === 'onNode') {
                    const focusNode = String(node).split(',')[0];
                    const foundNode = updatedNodes.find(n => n.topicId == focusNode);

                    if (foundNode) {
                        focusOnInit(foundNode.x, foundNode.y);
                        graphData.setGraph(foundNode.topicId, foundNode.name, null, null);
                    } else {
                        // Fallback to 'full' focus
                        applyFullFocus();
                        graphData.setGraph(null, null, null, null);
                    }
                } else if (cameraFocus === 'center') {
                    // Handle 'center' logic here
                } else if (cameraFocus === 'full') {
                    applyFullFocus();
                } else {
                    console.log("Camera Focus Type : " + cameraFocus + " not found (or no node to focus on), applying full focus by default");
                    applyFullFocus();
                }

                minX = Math.max(screenX - 300, 100);
                maxX = screenX + 300;
                minY = Math.max(screenY - 170, 100);
                maxY = screenY + 170;

                setBoundaries({ minX, maxX, minY, maxY });
                setDefaultBoundaries({ minX, maxX, minY, maxY });
                setGraphSize({ w: graphWidth, h: graphHeight });

                // Set path name as track name so progress tracker has something to show.
                if (data.trackPathName !== '') {
                    setTrackName(data.trackPathName);
                }
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [location.search]);

    React.useEffect(() => {

        if (loading) return; // Loading is checked to prevent multiple triggers before the graph is ready

        if (user?.topicStatus) {
            const updatedNodes = assignTopicStatus(nodes);
            const updatedEdges = assignEdgeType(edges, updatedNodes);
            setNodes(updatedNodes);
            setEdges(updatedEdges);
        }
    }, [user?.topicStatus]);

    React.useEffect(() => {
        if (loading) return; // Loading is checked to prevent multiple triggers before the graph is ready

        if (nodes.length > 0) {
            graphData.setGraph(null, null, nodes, edges)
        }
    }, [nodes]);

    const computeGraphDimensions = (nodes) => {
        const allXCoordinates = nodes.map(node => node.x);
        const allYCoordinates = nodes.map(node => node.y);

        const minX = Math.min(...allXCoordinates);
        const maxX = Math.max(...allXCoordinates);
        const minY = Math.min(...allYCoordinates);
        const maxY = Math.max(...allYCoordinates);

        const graphWidth = Math.max(maxX - minX, 200);
        const graphHeight = Math.max(maxY - minY, 200) ;

        return { graphWidth, graphHeight };
    }

    React.useEffect(() => {
        if (loading) return; // Loading is checked to prevent multiple triggers before the graph is ready

        if (selectedNode) {
            setModalOpen(true);
        }
    }, [selectedNode]);

    const closeModal = () => {
        setModalOpen(false);
        setSelectedNode(null);
        setModalContent('topic');
    };

    const handleExerciseClick = () => {
        setModalContent('exercise');
        //setModalOpen(true);
    };

    const handleTestClick = () => {
        setModalContent('test');
        //setModalOpen(true);
    };

    const handleBackToTopic = () => {
        console.log("clicked");
        setModalContent('topic'); // Set modalContent to 'topic'
    };

    return (
        <div>
            <GraphViewPort
                viewPortSize={viewPortSize}
                nodes={nodes}
                edges={edges}
                minScale={minScale}
                initScale={initScale}
                isHorizontal={isHorizontal}
            />

            {/*<Modal*/}
            {/*    isOpen={modalOpen}*/}
            {/*    onClose={closeModal}*/}
            {/*    content={*/}
            {/*        <Topic*/}
            {/*            topic={selectedNode}*/}
            {/*            onCloseModal={closeModal}*/}
            {/*            onExerciseClick={handleExerciseClick}*/}
            {/*            onTestClick={handleTestClick}*/}
            {/*        />*/}
            {/*    }*/}
            {/*/>*/}

            <Modal
                isOpen={modalOpen}
                onClose={closeModal}
                content={
                    modalContent === 'exercise' ? (
                        <Exercise topicId={selectedNode} type_={modalContent} onBackToTopic={handleBackToTopic} />
                    ) : modalContent === 'test' ? (
                        <Exercise topicId={selectedNode} type_={modalContent} onBackToTopic={handleBackToTopic} />
                    ) : (
                        <Topic
                            topic={selectedNode}
                            onCloseModal={closeModal}
                            onExerciseClick={handleExerciseClick}
                            onTestClick={handleTestClick}
                        />
                    )
                }
                containerPadding={modalContent === 'exercise' || modalContent === 'test' ? false : true}
                clickOutsideClose={modalContent === 'exercise' || modalContent === 'test' ? false : true}
            />


        </div>
    );
};

export default Graph;